import { useCallback, useEffect, useMemo, useState } from "react";

import type { Props } from "./types";

import PaginationMarkup from "./markup";

function Pagination(props: Props) {
	const [itemsPerPage, setItemsPerPage] = useState(props.itemsPerPage);
	const [pages, setPages] = useState<number[]>([]);

	const currentPage = Number(props.currentPage || 1);
	const { totalItems, canSetItemsPerPage, pageSizes = [20, 50, 100] } = props;
	const totalPages = useMemo(
		() => (itemsPerPage === 0 ? 0 : Math.ceil(totalItems / itemsPerPage)),
		[totalItems, itemsPerPage],
	);

	const setPage = useCallback(
		function (page: number) {
			const maxPage = totalPages || 1;
			if (page < 1) page = 1;
			else if (page > maxPage) page = maxPage;
			if (props.pageChanged) props.pageChanged(page);
		},
		[props.pageChanged, totalPages],
	);

	useEffect(() => {
		if (canSetItemsPerPage) {
			props.setItemsPerPage(itemsPerPage);
		}
	}, [itemsPerPage]);

	useEffect(() => {
		if (currentPage !== 1) setPage(1);
	}, [props.itemsPerPage]);

	useEffect(() => {
		const newPages: number[] = [];
		const ellipsis = -1;

		// Show all pages if total pages is 7 or less
		if (totalPages <= 7) {
			for (let i = 1; i <= totalPages; i++) {
				newPages.push(i);
			}
		}
		// Show first 4 pages + ellipsis + last 2 pages when near the beginning
		else if (currentPage <= 3) {
			for (let i = 1; i <= 4; i++) {
				newPages.push(i);
			}
			newPages.push(ellipsis);
			newPages.push(totalPages - 1);
			newPages.push(totalPages);
		}
		// Show first 2 pages + ellipsis + last 4 pages when near the end
		else if (currentPage >= totalPages - 3) {
			newPages.push(1);
			newPages.push(2);
			newPages.push(ellipsis);
			for (let i = totalPages - 3; i <= totalPages; i++) {
				newPages.push(i);
			}
		}
		// Show first page + ellipsis + current page ±1 + ellipsis + last page when in the middle
		else {
			newPages.push(1);
			newPages.push(ellipsis);
			newPages.push(currentPage - 1);
			newPages.push(currentPage);
			newPages.push(currentPage + 1);
			newPages.push(ellipsis);
			newPages.push(totalPages);
		}

		setPages(newPages);
	}, [totalPages, currentPage]);

	return (
		<PaginationMarkup
			{...props}
			currentPage={currentPage}
			pageSizes={pageSizes}
			itemsPerPage={itemsPerPage}
			pages={pages}
			totalPages={totalPages}
			setPage={setPage}
			setItemsPerPage={setItemsPerPage}
		/>
	);
}

export default Pagination;
